import type { GridSelection } from "@glideapps/glide-data-grid";
import {
  AI_SPREADSHEET_VALUE_TYPE,
  isAiSpreadsheetNumericValueType,
  type IAiSpreadsheetCell,
} from "@/payroll/ai-script/types";
import {
  formatAiSpreadsheetValue,
  parseAiSpreadsheetDateValue,
} from "./cell-display-value";
import { getSelectedCellCoordinates } from "./cell-selection";

export const SELECTION_AGGREGATE = {
  sum: "sum",
  average: "average",
  min: "min",
  max: "max",
  count: "count",
} as const;

export type SelectionAggregate =
  (typeof SELECTION_AGGREGATE)[keyof typeof SELECTION_AGGREGATE];

export const SELECTION_AGGREGATE_LABELS: Record<SelectionAggregate, string> = {
  [SELECTION_AGGREGATE.sum]: "Sum",
  [SELECTION_AGGREGATE.average]: "Average",
  [SELECTION_AGGREGATE.min]: "Min",
  [SELECTION_AGGREGATE.max]: "Max",
  [SELECTION_AGGREGATE.count]: "Count",
};

const EXCEL_SIGNIFICANT_DIGITS = 15;

type SelectionValueFamily = "numeric" | "date" | "other";

export interface SelectionSummaryOption {
  aggregate: SelectionAggregate;
  result: string;
}

export interface SelectionSummary {
  options: readonly SelectionSummaryOption[];
  defaultOption: SelectionSummaryOption;
}

interface ComparableValue {
  sortValue: number;
  displayValue: string | number;
}

interface BuildSelectionSummaryParams {
  selection: GridSelection;
  rowCount: number;
  columnCount: number;
  getCellRecord: (
    colIndex: number,
    rowIndex: number,
  ) => IAiSpreadsheetCell | undefined;
}

const formatGeneralNumber = (value: number) =>
  Number.isInteger(value)
    ? String(value)
    : String(Number(value.toPrecision(EXCEL_SIGNIFICANT_DIGITS)));

const getValueFamily = (cell: IAiSpreadsheetCell): SelectionValueFamily => {
  if (isAiSpreadsheetNumericValueType(cell.value_type)) return "numeric";
  if (cell.value_type === AI_SPREADSHEET_VALUE_TYPE.date) return "date";
  return "other";
};

const hasValue = (
  cell: IAiSpreadsheetCell | undefined,
): cell is IAiSpreadsheetCell =>
  !!cell &&
  (cell.computed_value !== null && cell.computed_value !== undefined
    ? String(cell.computed_value) !== ""
    : (cell.raw_input ?? "") !== "");

const getComparableValue = (
  cell: IAiSpreadsheetCell,
  family: SelectionValueFamily | undefined,
): ComparableValue | null => {
  if (
    cell.computed_value === null ||
    cell.computed_value === undefined ||
    typeof cell.computed_value === "boolean"
  ) {
    return null;
  }

  if (family === "date") {
    const date = parseAiSpreadsheetDateValue(cell.computed_value);
    return date
      ? {
          sortValue: date.valueOf(),
          displayValue: cell.computed_value,
        }
      : null;
  }

  const numeric = Number(cell.computed_value);
  if (Number.isFinite(numeric)) {
    return { sortValue: numeric, displayValue: numeric };
  }

  return null;
};

export const buildSelectionSummary = ({
  selection,
  rowCount,
  columnCount,
  getCellRecord,
}: BuildSelectionSummaryParams): SelectionSummary | null => {
  const coordinates = getSelectedCellCoordinates(
    selection,
    rowCount,
    columnCount,
  );
  if (coordinates.length <= 1) return null;

  const selectionSize = coordinates.length;
  const populatedCells: IAiSpreadsheetCell[] = [];
  coordinates.forEach(([col, row]) => {
    const cell = getCellRecord(col, row);
    if (hasValue(cell)) populatedCells.push(cell);
  });

  const families = new Set(populatedCells.map(getValueFamily));
  const isSingleFamily = families.size === 1;
  const family = isSingleFamily ? families.values().next().value : undefined;
  const countOption: SelectionSummaryOption = {
    aggregate: SELECTION_AGGREGATE.count,
    result: String(selectionSize),
  };

  if (family !== "numeric" && family !== "date") {
    return { options: [countOption], defaultOption: countOption };
  }

  const values = populatedCells.map((cell) => getComparableValue(cell, family));
  const hasInvalidComparableValue = values.some((value) => value === null);
  if (hasInvalidComparableValue) {
    return { options: [countOption], defaultOption: countOption };
  }

  const representativeFormatCode = populatedCells[0]?.format?.format_code;
  const formatResult = (value: string | number) =>
    representativeFormatCode
      ? formatAiSpreadsheetValue(value, representativeFormatCode)
      : family === "numeric" && typeof value === "number"
        ? formatGeneralNumber(value)
        : String(value);
  const comparableValues = values.filter(
    (value): value is ComparableValue => value !== null,
  );
  const aggregates = comparableValues.reduce(
    (current, value) => ({
      sum: current.sum + value.sortValue,
      min: value.sortValue < current.min.sortValue ? value : current.min,
      max: value.sortValue > current.max.sortValue ? value : current.max,
    }),
    { sum: 0, min: comparableValues[0]!, max: comparableValues[0]! },
  );

  const minOption: SelectionSummaryOption = {
    aggregate: SELECTION_AGGREGATE.min,
    result: formatResult(aggregates.min.displayValue),
  };
  const maxOption: SelectionSummaryOption = {
    aggregate: SELECTION_AGGREGATE.max,
    result: formatResult(aggregates.max.displayValue),
  };
  let options: readonly SelectionSummaryOption[];

  if (family === "numeric") {
    options = [
      {
        aggregate: SELECTION_AGGREGATE.sum,
        result: formatResult(aggregates.sum),
      },
      {
        aggregate: SELECTION_AGGREGATE.average,
        result: formatResult(aggregates.sum / comparableValues.length),
      },
      minOption,
      maxOption,
      countOption,
    ];
  } else {
    options = [minOption, maxOption, countOption];
  }

  return {
    options,
    defaultOption: options[0]!,
  };
};
